import { useState } from "react";
import { Calendar, X } from "lucide-react";

/**
 * Compact date-range filter pill suitable for any list-page toolbar.
 *
 * Usage:
 *   const [range, setRange] = useState({ from: "", to: "" });
 *   <DateRangeFilter value={range} onChange={setRange} testidPrefix="estimates" />
 *   ...
 *   const filtered = list.filter(r => inRange(r.created_at, range));
 *
 * Helper `inRange()` is exported for filter-on-array logic. Pass any
 * `YYYY-MM-DD` or full ISO timestamp; both work.
 */
export function DateRangeFilter({ value = { from: "", to: "" }, onChange, testidPrefix = "daterange", label = "Date" }) {
  const [open, setOpen] = useState(false);
  const active = !!(value.from || value.to);

  const summary = (() => {
    if (!active) return label;
    if (value.from && value.to) return `${value.from} → ${value.to}`;
    if (value.from) return `From ${value.from}`;
    return `Up to ${value.to}`;
  })();

  return (
    <div className="relative inline-flex items-stretch">
      <button
        type="button"
        data-testid={`${testidPrefix}-daterange-trigger`}
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold border ${
          active ? "border-[#F97316] text-[#F97316] bg-orange-50" : "border-slate-300 text-slate-600 bg-white hover:border-slate-400"
        }`}
        title="Filter rows by creation date">
        <Calendar size={12}/> {summary}
      </button>
      {active && (
        <button
          type="button"
          data-testid={`${testidPrefix}-daterange-clear`}
          onClick={() => onChange({ from: "", to: "" })}
          className="px-2 border border-l-0 border-[#F97316] text-[#F97316] bg-orange-50 hover:bg-orange-100"
          title="Clear date filter">
          <X size={11}/>
        </button>
      )}
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 shadow-lg p-3 w-72">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">{label} Range</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">From</span>
              <input
                type="date"
                data-testid={`${testidPrefix}-daterange-from`}
                value={value.from || ""}
                onChange={e => onChange({ ...value, from: e.target.value })}
                className="w-full text-xs border border-slate-300 px-2 py-1 focus:border-[#F97316] focus:outline-none"/>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">To</span>
              <input
                type="date"
                data-testid={`${testidPrefix}-daterange-to`}
                value={value.to || ""}
                onChange={e => onChange({ ...value, to: e.target.value })}
                className="w-full text-xs border border-slate-300 px-2 py-1 focus:border-[#F97316] focus:outline-none"/>
            </label>
          </div>
          <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100 text-[10px] uppercase tracking-wider font-bold">
            <div className="flex items-center gap-1">
              {[["Today", 0], ["7d", 7], ["30d", 30], ["90d", 90]].map(([lbl, days]) => (
                <button key={lbl}
                  type="button"
                  onClick={() => {
                    const to = new Date().toISOString().slice(0, 10);
                    const d = new Date(); d.setDate(d.getDate() - days);
                    const from = d.toISOString().slice(0, 10);
                    onChange({ from, to });
                  }}
                  className="text-slate-500 hover:text-[#F97316] px-1">
                  {lbl}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => setOpen(false)} className="text-[#F97316]">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Returns true if `dateStr` (YYYY-MM-DD or ISO) falls within the inclusive range. */
export function inRange(dateStr, range = {}) {
  if (!range || (!range.from && !range.to)) return true;
  if (!dateStr) return false;
  const ymd = String(dateStr).slice(0, 10);
  if (range.from && ymd < range.from) return false;
  if (range.to && ymd > range.to) return false;
  return true;
}
